import { type Theme } from '#app/theme/index.js';
import { type ExcludeRelayFutureAddedValue } from '#app/types/utils.js';
import DefaultPlaceholder from '../images/project-cover-placeholder.svg';
import SHAPE_COINS_DARK from '../images/SHAPE_COINS_DARK@2x.webp';
import SHAPE_COINS_LIGHT from '../images/SHAPE_COINS_LIGHT@2x.webp';
import SHAPE_COLLABORATION_DARK from '../images/SHAPE_COLLABORATION_DARK@2x.webp';
import SHAPE_COLLABORATION_LIGHT from '../images/SHAPE_COLLABORATION_LIGHT@2x.webp';
import SHAPE_FOCUS_DARK from '../images/SHAPE_FOCUS_DARK@2x.webp';
import SHAPE_FOCUS_LIGHT from '../images/SHAPE_FOCUS_LIGHT@2x.webp';
import SHAPE_GAIN_DARK from '../images/SHAPE_GAIN_DARK@2x.webp';
import SHAPE_GAIN_LIGHT from '../images/SHAPE_GAIN_LIGHT@2x.webp';
import SHAPE_STAIRS_DARK from '../images/SHAPE_STAIRS_DARK@2x.webp';
import SHAPE_STAIRS_LIGHT from '../images/SHAPE_STAIRS_LIGHT@2x.webp';
import SHAPE_WAVE_DARK from '../images/SHAPE_WAVE_DARK@2x.webp';
import SHAPE_WAVE_LIGHT from '../images/SHAPE_WAVE_LIGHT@2x.webp';
import SVG_BLUE2 from '../images/SVG_BLUE2.svg';
import SVG_BLUE from '../images/SVG_BLUE.svg';
import SVG_LINES_BLUE from '../images/SVG_LINES_BLUE.svg';
import SVG_LINES_GREEN from '../images/SVG_LINES_GREEN.svg';
import SVG_LINES_ORANGE from '../images/SVG_LINES_ORANGE.svg';
import SVG_LINES_PURPLE from '../images/SVG_LINES_PURPLE.svg';
import SVG_LINES_SALMON from '../images/SVG_LINES_SALMON.svg';
import SVG_LINES_YELLOW from '../images/SVG_LINES_YELLOW.svg';
import SVG_WHITE2 from '../images/SVG_WHITE2.svg';
import SVG_WHITE from '../images/SVG_WHITE.svg';
import SVG_YELLOW2 from '../images/SVG_YELLOW2.svg';
import SVG_YELLOW from '../images/SVG_YELLOW.svg';
import VAPOR_GRADIENT_BLUE_DARK from '../images/VAPOR_GRADIENT_BLUE_DARK@2x.webp';
import VAPOR_GRADIENT_BLUE_LIGHT from '../images/VAPOR_GRADIENT_BLUE_LIGHT@2x.webp';
import VAPOR_GRADIENT_GREEN_DARK from '../images/VAPOR_GRADIENT_GREEN_DARK@2x.webp';
import VAPOR_GRADIENT_GREEN_LIGHT from '../images/VAPOR_GRADIENT_GREEN_LIGHT@2x.webp';
import VAPOR_GRADIENT_PURPLE_DARK from '../images/VAPOR_GRADIENT_PURPLE_DARK@2x.webp';
import VAPOR_GRADIENT_PURPLE_LIGHT from '../images/VAPOR_GRADIENT_PURPLE_LIGHT@2x.webp';
import VAPOR_GRADIENT_SALMON_DARK from '../images/VAPOR_GRADIENT_SALMON_DARK@2x.webp';
import VAPOR_GRADIENT_SALMON_LIGHT from '../images/VAPOR_GRADIENT_SALMON_LIGHT@2x.webp';
import VAPOR_GRADIENT_WATERMELON_DARK from '../images/VAPOR_GRADIENT_WATERMELON_DARK@2x.webp';
import VAPOR_GRADIENT_WATERMELON_LIGHT from '../images/VAPOR_GRADIENT_WATERMELON_LIGHT@2x.webp';
import VAPOR_GRADIENT_WHITE_DARK from '../images/VAPOR_GRADIENT_WHITE_DARK@2x.webp';
import VAPOR_GRADIENT_YELLOW_LIGHT from '../images/VAPOR_GRADIENT_YELLOW_LIGHT@2x.webp';
import { type ProjectCoverTextCoverStyle } from '../types.js';

export const getBackgroundStyles = (
  theme: Theme,
  textCoverStyle?: null | ProjectCoverTextCoverStyle,
) => {
  const fallbackBackgroundColor = theme.colorsV2.gray05;
  const fallbackBackgroundImage = DefaultPlaceholder;

  if (!textCoverStyle || textCoverStyle === '%future added value') {
    return {
      color: fallbackBackgroundColor,
      image: fallbackBackgroundImage,
    };
  }

  const BG_STYLE_MAP: {
    [K in ExcludeRelayFutureAddedValue<ProjectCoverTextCoverStyle>]: {
      color?: string;
      image?: string;
    };
  } = {
    COLOR_BLACK: {
      color: theme.colorsV2.black,
    },
    COLOR_BLUE: {
      color: theme.colors.brandBlue,
    },
    COLOR_GREEN: {
      color: theme.colors.brandGreen,
    },
    COLOR_ORANGE: {
      color: theme.colors.brandOrange,
    },
    COLOR_PURPLE: {
      color: theme.colors.brandPurple,
    },
    COLOR_SALMON: {
      color: theme.colors.brandSalmon,
    },
    COLOR_WATERMELON: {
      color: theme.colors.brandWatermelon,
    },
    COLOR_WHITE: {
      color: theme.colorsV2.white,
    },
    COLOR_YELLOW: {
      color: theme.colorsV2.yellow40,
    },
    SHAPE_COINS_DARK: {
      color: theme.colorsV2.white,
      image: SHAPE_COINS_DARK,
    },
    SHAPE_COINS_LIGHT: {
      color: theme.colorsV2.black,
      image: SHAPE_COINS_LIGHT,
    },
    SHAPE_COLLABORATION_DARK: {
      color: theme.colorsV2.white,
      image: SHAPE_COLLABORATION_DARK,
    },
    SHAPE_COLLABORATION_LIGHT: {
      color: theme.colorsV2.black,
      image: SHAPE_COLLABORATION_LIGHT,
    },
    SHAPE_FOCUS_DARK: {
      color: theme.colorsV2.white,
      image: SHAPE_FOCUS_DARK,
    },
    SHAPE_FOCUS_LIGHT: {
      color: theme.colorsV2.black,
      image: SHAPE_FOCUS_LIGHT,
    },
    SHAPE_GAIN_DARK: {
      color: theme.colorsV2.white,
      image: SHAPE_GAIN_DARK,
    },
    SHAPE_GAIN_LIGHT: {
      color: theme.colorsV2.black,
      image: SHAPE_GAIN_LIGHT,
    },
    SHAPE_STAIRS_DARK: {
      color: theme.colorsV2.white,
      image: SHAPE_STAIRS_DARK,
    },
    SHAPE_STAIRS_LIGHT: {
      color: theme.colorsV2.black,
      image: SHAPE_STAIRS_LIGHT,
    },
    SHAPE_WAVE_DARK: {
      color: theme.colorsV2.white,
      image: SHAPE_WAVE_DARK,
    },
    SHAPE_WAVE_LIGHT: {
      color: theme.colorsV2.black,
      image: SHAPE_WAVE_LIGHT,
    },
    SVG_BLUE: {
      color: theme.colors.brandBlue,
      image: SVG_BLUE,
    },
    SVG_BLUE2: {
      color: theme.colors.brandBlue,
      image: SVG_BLUE2,
    },
    SVG_LINES_BLUE: {
      color: theme.colorsV2.black,
      image: SVG_LINES_BLUE,
    },
    SVG_LINES_GREEN: {
      color: theme.colorsV2.black,
      image: SVG_LINES_GREEN,
    },
    SVG_LINES_ORANGE: {
      color: theme.colorsV2.black,
      image: SVG_LINES_ORANGE,
    },
    SVG_LINES_PURPLE: {
      color: theme.colorsV2.black,
      image: SVG_LINES_PURPLE,
    },
    SVG_LINES_SALMON: {
      color: theme.colorsV2.black,
      image: SVG_LINES_SALMON,
    },
    SVG_LINES_YELLOW: {
      color: theme.colorsV2.black,
      image: SVG_LINES_YELLOW,
    },
    SVG_WHITE: {
      color: theme.colorsV2.white,
      image: SVG_WHITE,
    },
    SVG_WHITE2: {
      color: theme.colorsV2.white,
      image: SVG_WHITE2,
    },
    SVG_YELLOW: {
      color: theme.colorsV2.yellow40,
      image: SVG_YELLOW,
    },
    SVG_YELLOW2: {
      color: theme.colorsV2.yellow40,
      image: SVG_YELLOW2,
    },
    VAPOR_GRADIENT_BLUE_DARK: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_BLUE_DARK,
    },
    VAPOR_GRADIENT_BLUE_LIGHT: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_BLUE_LIGHT,
    },
    VAPOR_GRADIENT_GREEN_DARK: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_GREEN_DARK,
    },
    VAPOR_GRADIENT_GREEN_LIGHT: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_GREEN_LIGHT,
    },
    VAPOR_GRADIENT_PURPLE_DARK: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_PURPLE_DARK,
    },
    VAPOR_GRADIENT_PURPLE_LIGHT: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_PURPLE_LIGHT,
    },
    VAPOR_GRADIENT_SALMON_DARK: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_SALMON_DARK,
    },
    VAPOR_GRADIENT_SALMON_LIGHT: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_SALMON_LIGHT,
    },
    VAPOR_GRADIENT_WATERMELON_DARK: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_WATERMELON_DARK,
    },
    VAPOR_GRADIENT_WATERMELON_LIGHT: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_WATERMELON_LIGHT,
    },
    VAPOR_GRADIENT_WHITE_DARK: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_WHITE_DARK,
    },
    VAPOR_GRADIENT_YELLOW_LIGHT: {
      color: theme.colorsV2.black,
      image: VAPOR_GRADIENT_YELLOW_LIGHT,
    },
  };

  for (const [name, style] of Object.entries(BG_STYLE_MAP)) {
    if (style.image?.startsWith('data:')) {
      throw new Error(`Expected image URL; received data: ("${name}")`);
    }
  }

  const coverCandidate = BG_STYLE_MAP[textCoverStyle];

  return {
    color: coverCandidate.color ?? fallbackBackgroundColor,
    image: coverCandidate.image ?? fallbackBackgroundImage,
  };
};
